<template>
    <Card pageTitle="Create Purchase Order">
        <div>
            <TabContainer @change="handleChangeTab" :tabs="Object.values(STOCK_IMPORT_TABS)" :selectedTab="selectedTab" />

            <ImportErrorModal
                :show="showImportResult"
                :importStatus="importStatus"
                :importSuccess="importSuccess"
                :importErrors="importErrors"
                :processing="processing"
                @close="handleImportModalClose"
            >
                <div v-if="importWarnings.length" class="import-warning-panel">
                    <h5>Import warnings</h5>
                    <div v-for="warning in importWarnings" :key="warning.id" class="import-warning-row">
                        <i class="material-icons">report_problem</i>
                        <span>{{ warning.message }}</span>
                    </div>
                    <router-link v-if="createdPurchaseOrderName" :to="`/purchase-orders/${createdPurchaseOrderName}/receive`">
                        <Button class="mt-2">Review Purchase Order</Button>
                    </router-link>
                </div>
                <router-link to="/inventory-manager">
                    <Button>View Inventory</Button>
                </router-link>
                <router-link to="/import-history">
                    <Button class="mx-2">View Import History</Button>
                </router-link>
            </ImportErrorModal>

            <AllowPermission
                :permission="
                    selectedTab.value == STOCK_IMPORT_TABS.MANUAL.value
                        ? PERMISSION_CODES.CMS_CREATE_PURCHASE_ORDER
                        : PERMISSION_CODES.CMS_ASN_STOCK_IMPORT
                "
            >
                <!-- CSV Import Form (ASN) -->
                <form 
                    v-if="selectedTab.value !== STOCK_IMPORT_TABS.MANUAL.value" 
                    style="" 
                    class="my-3" 
                    @submit.prevent="handleImport"
                >
                    <div class="row">
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Select Supplier*"
                                :options="suppliers.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="supplierId"
                                @change="supplierId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Select Location*"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="warehouseId"
                                @change="warehouseId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <div class="mt-3">
                                <p class="mb-1">Select Excel or CSV File To Import* (max 2.5mb)</p>
                                <input
                                    @click="$event.target.value = null"
                                    ref="inputRef"
                                    @change="handleSelectCsvFile"
                                    class="form-control form-control-sm"
                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                    type="file"
                                />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="tax" placeholder="TAX" type="number" label="TAX" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput
                                v-model="supplierInvoiceNumber"
                                placeholder="Enter value"
                                type="text"
                                label="Supplier Invoice Number"
                            />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="shippingCost" placeholder="Enter value" type="number" label="Shipping Cost" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="shippingMethod" placeholder="Enter value" type="text" label="Shipping Method" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="trackingNumber" placeholder="Enter value" type="text" label="Tracking Number" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="notes" placeholder="Enter value" type="text" label="Notes" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="eta" placeholder="Select ETA date" type="date" label="Expected Arrival Date (ETA)" />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Bill To Location"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="billToLocationId"
                                @change="billToLocationId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Ship To Location"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="shipToLocationId"
                                @change="shipToLocationId = $event"
                            />
                        </div>
                    </div>
                    <Button>{{ processing ? "Please wait..." : "Create Purchase Order" }}</Button>
                </form>

                <!-- Manual Purchase Order Form -->
                <form 
                    v-if="selectedTab.value === STOCK_IMPORT_TABS.MANUAL.value" 
                    class="my-3" 
                    @submit.prevent="handleManualCreate"
                >
                    <div class="row">
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Select Supplier*"
                                :options="suppliers.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="supplierId"
                                @change="supplierId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Select To Warehouse*"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="warehouseId"
                                @change="warehouseId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="tax" placeholder="TAX" type="number" label="TAX" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput
                                v-model="supplierInvoiceNumber"
                                placeholder="Enter value"
                                type="text"
                                label="Supplier Invoice Number"
                            />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="shippingCost" placeholder="Enter value" type="number" label="Shipping Cost" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="shippingMethod" placeholder="Enter value" type="text" label="Shipping Method" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="trackingNumber" placeholder="Enter value" type="text" label="Tracking Number" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="notes" placeholder="Enter value" type="text" label="Notes" />
                        </div>
                        <div class="col-md-6">
                            <CustomInput v-model="eta" placeholder="Select ETA date" type="date" label="Expected Arrival Date (ETA)" />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Bill To Location"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="billToLocationId"
                                @change="billToLocationId = $event"
                            />
                        </div>
                        <div class="col-md-6">
                            <SelectDropdown
                                label="Ship To Location"
                                :options="warehouses.map((item) => ({ id: item.id, title: item.name }))"
                                :multiple="false"
                                :value="shipToLocationId"
                                @change="shipToLocationId = $event"
                            />
                        </div>
                    </div>

                    <!-- Purchase Order Items Section -->
                    <div class="mt-4">
                        <h5>Purchase Order Items</h5>
                        <div class="border p-3">
                            <div v-if="manualPOItems.length === 0" class="text-center text-muted py-3">
                                No items added yet. Click "Add Item" to start building your purchase order.
                            </div>
                            
                            <div v-for="(item, index) in manualPOItems" :key="index" class="row border-bottom py-2">
                                <div class="col-md-3">
                                    <SelectDropdown
                                        label="Select Product Variant*"
                                        :options="products"
                                        :multiple="false"
                                        :internalSearch="false"
                                        :loading="loadingProducts"
                                        :value="item.product_id"
                                        placeholder="Type at least 2 characters"
                                        @change="handleProductChange(index, $event)"
                                        @search-change="searchProducts"
                                    />
                                    <small class="text-muted">Search by product name, SKU, item code, or UPC</small>
                                </div>
                                <div class="col-md-2">
                                    <CustomInput 
                                        v-model="item.sku" 
                                        placeholder="SKU" 
                                        label="Supplier SKU" 
                                    />
                                </div>
                                <div class="col-md-2">
                                    <CustomInput 
                                        v-model="item.shipped_qty" 
                                        placeholder="Quantity*" 
                                        type="number" 
                                        label="Quantity" 
                                        :required="true"
                                    />
                                </div>
                                <div class="col-md-2">
                                    <CustomInput 
                                        v-model="item.unit_price" 
                                        placeholder="Unit Price*" 
                                        type="number" 
                                        step="0.01" 
                                        label="Unit Price" 
                                        :required="true"
                                    />
                                </div>
                                <div class="col-md-2">
                                    <CustomInput 
                                        v-model="item.batch_lot" 
                                        placeholder="Batch/Lot" 
                                        label="Batch/Lot #" 
                                    />
                                </div>
                                <div class="col-md-1 d-flex align-items-end">
                                    <button 
                                        type="button" 
                                        @click="removeManualItem(index)" 
                                        class="btn btn-sm btn-danger"
                                        title="Remove Item"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            
                            <div class="mt-3">
                                <button type="button" @click="addManualItem" class="btn btn-sm btn-secondary">
                                    + Add Item
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="mt-3">
                        <Button :disabled="manualPOItems.length === 0">
                            {{ processing ? "Please wait..." : "Create Purchase Order" }}
                        </Button>
                    </div>
                </form>
            </AllowPermission>
        </div>
    </Card>
</template>

<script>
import CustomInput from "../../components/crud/custom-input.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import Button from "../../components/UI/Button.vue"
import { mapState } from "vuex"
import { http } from "../../utils/http"
import { errMsg } from "../../utils/common"
import CustomModal from "../../components/layout/CustomModal.vue"
import ImportErrorModal from "../../components/layout/ImportErrorModal.vue"
import Card from "../../components/UI/Card.vue"
import TabContainer from "../../components/UI/TabContainer.vue"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"

const STOCK_IMPORT_TABS = {
    ASN: {
        name: "ASN",
        value: "ASN Stock Import",
    },
    MANUAL: {
        name: "Manual PO",
        value: "Manual Purchase Order",
    },
}

export default {
    components: {
        TabContainer,
        Card,
        CustomInput,
        SelectDropdown,
        Button,
        CustomModal,
        ImportErrorModal,
        AllowPermission,
    },
    computed: { ...mapState("misc", ["warehouses", "suppliers"]) },
    data() {
        return {
            PERMISSION_CODES,
            STOCK_IMPORT_TABS,
            selectedTab: STOCK_IMPORT_TABS.ASN,
            warehouseId: "",
            supplierId: "",
            tax: 0,
            supplierInvoiceNumber: "",
            shippingCost: 0,
            shippingMethod: "",
            trackingNumber: "",
            notes: "",
            eta: "",
            billToLocationId: "",
            shipToLocationId: "",
            csvFile: null,
            processing: false,
            showImportResult: false,
            importStatus: "Pending",
            importErrors: null,
            importWarnings: [],
            importSuccess: false,
            createdPurchaseOrderName: "",
            manualPOItems: [],
            products: [],
            productOptionCache: {},
            productSearchTimeout: null,
            productSearchRequestId: 0,
            loadingProducts: false,
        }
    },
    mounted() {
        if (this.redirectLegacyCovaTab()) return
    },
    methods: {
        redirectLegacyCovaTab() {
            if (this.$route.query.active_tab !== "COVA") return false
            this.$router.replace("/cova-import")
            return true
        },
        handleChangeTab(e) {
            if (!e) {
                if (this.redirectLegacyCovaTab()) return
                this.selectedTab = STOCK_IMPORT_TABS.ASN
                this.resetFields()
                return
            }
            this.selectedTab = e
            this.resetFields()
        },
        resetFields() {
            if (this.$refs.inputRef) {
                this.$refs.inputRef.value = null
            }
            this.warehouseId = ""
            this.supplierId = ""
            this.tax = 0
            this.supplierInvoiceNumber = ""
            this.shippingCost = 0
            this.shippingMethod = ""
            this.trackingNumber = ""
            this.notes = ""
            this.eta = ""
            this.billToLocationId = ""
            this.shipToLocationId = ""
            this.csvFile = null
            this.processing = false
            this.showImportResult = false
            this.importStatus = "Pending"
            this.importErrors = null
            this.importWarnings = []
            this.importSuccess = false
            this.createdPurchaseOrderName = ""
            this.manualPOItems = []
            this.products = []
            this.productOptionCache = {}
            clearTimeout(this.productSearchTimeout)
            this.productSearchRequestId += 1
            this.loadingProducts = false
        },
        handleImportModalClose() {
            if (this.importWarnings.length) {
                this.showImportResult = false
                return
            }
            if (this.importSuccess) {
                this.$router.push("/inventory-manager")
            } else {
                this.showImportResult = false
            }
        },
        normalizeWarningIssue(issue, index) {
            const code = issue && typeof issue === "object" ? issue.code || issue.key || `warning-${index}` : `warning-${index}`
            const rawMessage =
                issue && typeof issue === "object"
                    ? issue.message || issue.description || issue.reason || issue.code
                    : issue
            const message = rawMessage ? String(rawMessage) : JSON.stringify(issue)
            return {
                id: `${code}-${index}`,
                code,
                message,
            }
        },
        normalizeWarningList(warnings) {
            if (!Array.isArray(warnings)) return []
            return warnings.map((warning, index) => this.normalizeWarningIssue(warning, index)).filter((warning) => warning.message)
        },
        uniqueWarnings(warnings) {
            const seen = new Set()
            return warnings.filter((warning) => {
                const key = `${warning.code}:${warning.message}`
                if (seen.has(key)) return false
                seen.add(key)
                return true
            })
        },
        extractResponseWarnings(res) {
            const data = (res && res.data) || {}
            const result = data.result || {}
            const createdPurchaseOrder = data.createdPurchaseOrder || {}
            return this.uniqueWarnings([
                ...this.normalizeWarningList(data.warnings),
                ...this.normalizeWarningList(result.warnings),
                ...this.normalizeWarningList(createdPurchaseOrder.warnings),
            ])
        },
        async handleImport() {
            const formData = new FormData()
            if (this.processing) return
            if (!this.supplierId) {
                return this.$toast.error("Please select supplier id")
            }
            if (!this.warehouseId) {
                return this.$toast.error("Please select supplier id")
            }
            if (!this.csvFile) {
                return this.$toast.error("Please select an Excel or CSV File!")
            }
            formData.append("supplier", this.supplierId)
            formData.append("location", this.warehouseId)

            formData.append("supplier_name", this.supplierId ? this.suppliers.find((item) => item.id == this.supplierId).name : "")
            formData.append("location_name", this.warehouseId ? this.warehouses.find((item) => item.id == this.warehouseId).name : "")

            formData.append("tax", this.tax)
            formData.append("supplierInvoiceNumber", this.supplierInvoiceNumber)
            formData.append("shippingCost", this.shippingCost)
            formData.append("shippingMethod", this.shippingMethod)
            formData.append("trackingNumber", this.trackingNumber)
            formData.append("notes", this.notes)
            formData.append("eta", this.eta)
            
            // Get warehouse details for bill to and ship to locations
            const billToWarehouse = this.billToLocationId ? this.warehouses.find(w => w.id == this.billToLocationId) : null
            const shipToWarehouse = this.shipToLocationId ? this.warehouses.find(w => w.id == this.shipToLocationId) : null
            
            formData.append("billToAddress", billToWarehouse ? `${billToWarehouse.name}\n${billToWarehouse.address || ''}` : "")
            formData.append("shipToAddress", shipToWarehouse ? `${shipToWarehouse.name}\n${shipToWarehouse.address || ''}` : "")
            formData.append("csvFile", this.csvFile)
            formData.append("covaMode", "0")

            this.processing = true
            this.importSuccess = false
            this.importWarnings = []
            this.createdPurchaseOrderName = ""
            this.showImportResult = true
            try {
                this.importStatus = "Importing..."
                const res = await http.post("/stock/import-asn", formData)
                const createdPurchaseOrder = res.data.createdPurchaseOrder || {}
                this.importWarnings = this.extractResponseWarnings(res)
                this.createdPurchaseOrderName = createdPurchaseOrder.name || ""
                if (!this.createdPurchaseOrderName) {
                    this.importWarnings = this.uniqueWarnings([
                        ...this.importWarnings,
                        this.normalizeWarningIssue(
                            {
                                code: "MISSING_CREATED_PURCHASE_ORDER",
                                message: "Import completed, but the response did not include a purchase order link. Review import history before receiving.",
                            },
                            this.importWarnings.length
                        ),
                    ])
                }
                this.importStatus = this.importWarnings.length ? "Imported with warnings" : "Success"
                this.importSuccess = true

                if (this.importWarnings.length) {
                    this.processing = false
                    this.$toast.warning("Imported with warnings. Review before receiving.")
                    return
                }

                this.$toast.success("Imported Successfully!")
                this.$router.push(`/purchase-orders/${this.createdPurchaseOrderName}/receive`)
            } catch (err) {
                console.log("error in importing asn", err)
                this.importErrors = err.response && err.response.data ? err.response.data.result || [] : []
                this.$toast.error(errMsg(err, "Could not import stock!"))
                this.importStatus = "Failed"
            }
            this.processing = false
        },

        handleSelectCsvFile(e) {
            const file = e.target.files[0]
            if (file.size > 2.5 * 1024 * 1024) {
                e.target.value = null
                return this.$toast.error("File size cannot be greater than 2.5 mb!")
            }
            this.csvFile = file
        },

        selectedProductOptions() {
            return this.manualPOItems
                .map((item) => this.productOptionCache[item.product_id])
                .filter(Boolean)
        },

        mergeProductOptions(searchResults) {
            const merged = new Map()
            this.selectedProductOptions().forEach((product) => merged.set(product.id, product))
            searchResults.forEach((product) => merged.set(product.id, product))
            return [...merged.values()]
        },

        searchProducts(rawSearch) {
            clearTimeout(this.productSearchTimeout)
            this.productSearchRequestId += 1
            this.loadingProducts = false
            const search = `${rawSearch || ""}`.trim()

            if (search.length < 2) {
                this.products = this.selectedProductOptions()
                return
            }

            this.productSearchTimeout = setTimeout(() => this.fetchProducts(search), 350)
        },

        async fetchProducts(search) {
            const requestId = ++this.productSearchRequestId
            try {
                this.loadingProducts = true
                const res = await http.get("/items/dropdown", {
                    params: { search, limit: 50 },
                })
                if (requestId !== this.productSearchRequestId) return

                const results = res.data.result || []
                results.forEach((product) => this.$set(this.productOptionCache, product.id, product))
                this.products = this.mergeProductOptions(results)

                if (results.length === 0) {
                    this.$toast.info("No matching product variants found.")
                }
            } catch (err) {
                if (requestId !== this.productSearchRequestId) return
                console.error("Error fetching products:", err)
                this.$toast.error("Failed to load product variants")
                this.products = this.selectedProductOptions()
            } finally {
                if (requestId === this.productSearchRequestId) {
                    this.loadingProducts = false
                }
            }
        },

        // Handle product selection in dropdown
        handleProductChange(itemIndex, productId) {
            if (productId) {
                const selectedProduct = this.productOptionCache[productId] || this.products.find(p => p.id === productId)
                if (selectedProduct) {
                    // Update the item with selected product information
                    this.manualPOItems[itemIndex].product_id = productId
                    this.manualPOItems[itemIndex].item_name = selectedProduct.variant_name || selectedProduct.title
                    this.manualPOItems[itemIndex].item_code = selectedProduct.item_code
                    this.manualPOItems[itemIndex].sku = selectedProduct.supplier_sku || selectedProduct.item_code
                    
                    // Set catalog_sku to the item_code for stock confirmation
                    this.manualPOItems[itemIndex].catalog_sku = selectedProduct.item_code
                    
                    // Show a more informative message
                    this.$toast.success(`Selected: ${selectedProduct.title}`)
                }
            } else {
                // Clear product selection
                this.manualPOItems[itemIndex].product_id = ''
                this.manualPOItems[itemIndex].item_name = ''
                this.manualPOItems[itemIndex].item_code = ''
                this.manualPOItems[itemIndex].sku = ''
                this.manualPOItems[itemIndex].catalog_sku = ''
            }
        },

        // Manual Purchase Order Methods
        addManualItem() {
            this.manualPOItems.push({
                product_id: '', // Store the selected product ID
                item_name: '',  // Store the product name for backend compatibility
                item_code: '',  // Store the product code
                sku: '',
                catalog_sku: '', // Add catalog_sku field for stock confirmation
                shipped_qty: 1,
                unit_price: 0,
                batch_lot: '',
                room_name: 'Front Room',
                uom: 'EA'
            })
        },

        removeManualItem(index) {
            this.manualPOItems.splice(index, 1)
        },

        async handleManualCreate() {
            if (this.processing) return
            
            // Validation
            if (!this.supplierId) {
                return this.$toast.error("Please select a supplier")
            }
            if (!this.warehouseId) {
                return this.$toast.error("Please select a warehouse")
            }
            if (this.manualPOItems.length === 0) {
                return this.$toast.error("Please add at least one item")
            }

            // Validate each item
            for (let i = 0; i < this.manualPOItems.length; i++) {
                const item = this.manualPOItems[i]
                if (!item.product_id) {
                    return this.$toast.error(`Item ${i + 1}: Please select a product`)
                }
                if (!item.shipped_qty || item.shipped_qty <= 0) {
                    return this.$toast.error(`Item ${i + 1}: Quantity must be greater than 0`)
                }
                if (!item.unit_price || item.unit_price < 0) {
                    return this.$toast.error(`Item ${i + 1}: Unit price must be 0 or greater`)
                }
            }

            try {
                this.processing = true
                
                // Get warehouse details for addresses
                const billToWarehouse = this.billToLocationId ? this.warehouses.find(w => w.id == this.billToLocationId) : null
                const shipToWarehouse = this.shipToLocationId ? this.warehouses.find(w => w.id == this.shipToLocationId) : null
                const selectedSupplier = this.suppliers.find(s => s.id == this.supplierId)
                const selectedWarehouse = this.warehouses.find(w => w.id == this.warehouseId)

                // Process items with proper mapping
                const processedItems = this.manualPOItems.map((item, index) => {
                    return {
                        ...item,
                        name: this.generateRandomCode(15),
                        catalog_sku: item.catalog_sku || item.item_code || '',
                        vendor: selectedSupplier?.name || '',
                        brand: '',
                        case_gtin: '',
                        packaged_on_date: new Date().toISOString(),
                        gtin_barcode: '',
                        each_gtin: '',
                        uom_conversion: '',
                        uom_conversion_qty: 0,
                        shipment_id: '',
                        container_id: ''
                    }
                })

                const payload = {
                    supplier: this.supplierId,
                    supplier_name: selectedSupplier?.name || '',
                    to_warehouse: this.warehouseId,
                    target_warehouse_address: selectedWarehouse?.name || '',
                    tax: this.tax || 0,
                    supplier_invoice_no: this.supplierInvoiceNumber,
                    shipping_cost: this.shippingCost || 0,
                    shipping_method: this.shippingMethod,
                    tracking_number: this.trackingNumber,
                    notes: this.notes,
                    eta: this.eta,
                    bill_to_address: billToWarehouse ? `${billToWarehouse.name}\n${billToWarehouse.address || ''}` : '',
                    ship_to_address: shipToWarehouse ? `${shipToWarehouse.name}\n${shipToWarehouse.address || ''}` : '',
                    items: processedItems
                }

                const res = await http.post("/purchase-orders/manual", payload)
                this.$toast.success("Purchase Order created successfully!")
                this.$router.push(`/purchase-orders/${res.data.purchaseOrder.name}`)
                
            } catch (err) {
                console.error("Error creating manual purchase order:", err)
                this.$toast.error("Error creating purchase order: " + (err.response?.data?.message || err.message))
            } finally {
                this.processing = false
            }
        },

        generateRandomCode(length) {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
            let result = ''
            for (let i = 0; i < length; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length))
            }
            return result
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

/* Tokenize spacing for forms and sections */
form.my-3 {
  margin-top: $sectionGap !important;
  margin-bottom: $sectionGap !important;
}

.border {
  border-radius: $cardRadius;
  box-shadow: $cardShadow;
}

/* Align currency/qty inputs visually using component tokens */
.form-control.form-control-sm {
  font-size: $component-font-size;
  border-radius: $component-border-radius;
}

.import-warning-panel {
  border: 1px solid rgba($warningColor, 0.35);
  border-radius: $cardRadius;
  margin-bottom: 12px;
  padding: 12px;
  background: rgba($warningColor, 0.12);
  color: darken($warningColor, 28%);

  h5 {
    margin: 0 0 10px;
    font-size: 14px;
    font-weight: 700;
  }
}

.import-warning-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;

  i {
    font-size: 18px;
  }
}
</style>
